import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, Users, CreditCard, Calendar, MonitorPlay,
  Settings, LogOut, RefreshCw, SlidersHorizontal, UserCog,
} from 'lucide-react'
import { getPublicConfig } from '@/api/config'
import type { AuthState } from '@/hooks/useAuth'
import { useProviderTapTrigger } from '@/hooks/useProviderTrigger'

interface NavItem {
  title: string
  path: string
  icon: React.ReactNode
  permission?: string
}

const NAV_ITEMS: NavItem[] = [
  { title: 'Inicio',       path: '/dashboard', icon: <LayoutDashboard className="w-4 h-4 shrink-0" /> },
  { title: 'Miembros',     path: '/members',   icon: <Users           className="w-4 h-4 shrink-0" />, permission: 'users:read' },
  { title: 'Caja',         path: '/payments',  icon: <CreditCard      className="w-4 h-4 shrink-0" />, permission: 'payments:read' },
  { title: 'Eventos',      path: '/events',    icon: <Calendar        className="w-4 h-4 shrink-0" />, permission: 'events:read' },
  { title: 'Dispositivos', path: '/devices',   icon: <MonitorPlay     className="w-4 h-4 shrink-0" />, permission: 'devices:read' },
  { title: 'Sincronizar',  path: '/sync',      icon: <RefreshCw       className="w-4 h-4 shrink-0" />, permission: 'devices:read' },
]

const BOTTOM_ITEMS: NavItem[] = [
  { title: 'Configuración', path: '/settings',     icon: <Settings          className="w-4 h-4 shrink-0" />, permission: 'users:*' },
  { title: 'Usuarios',      path: '/admin/users',  icon: <UserCog           className="w-4 h-4 shrink-0" />, permission: 'users:write' },
  { title: 'Sistema',       path: '/admin/config', icon: <SlidersHorizontal className="w-4 h-4 shrink-0" />, permission: '*' },
]

interface SidebarProps {
  can: (permission: string) => boolean
  auth: AuthState
}

export function Sidebar({ can, auth }: SidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const active   = '/' + location.pathname.split('/')[1]
  const visible  = (item: NavItem) => !item.permission || can(item.permission)
  const initials = auth.user?.email?.slice(0, 2).toUpperCase() ?? '??'
  const handleLogoTap = useProviderTapTrigger()

  const [businessName, setBusinessName] = useState('Mi Gimnasio')
  const [businessLogoUrl, setBusinessLogoUrl] = useState<string | null>(null)
  useEffect(() => {
    getPublicConfig()
      .then(c => {
        if (c.business_name) setBusinessName(c.business_name)
        if (c.business_logo_url) setBusinessLogoUrl(c.business_logo_url)
      })
      .catch(() => {})
  }, [])

  const roleLabel: Record<string, string> = {
    admin: 'Administrador',
    recepcionista: 'Recepcionista',
    superadmin: 'Super Admin',
  }

  return (
    <aside
      className="w-56 flex flex-col shrink-0 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0c1c2e 0%, #0b2a1c 60%, #0e1a32 100%)' }}
    >
      {/* Subtle orb */}
      <div
        className="absolute -top-20 -left-20 w-64 h-64 rounded-full blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, hsl(160 84% 39% / 0.2) 0%, transparent 70%)' }}
      />

      <div className="relative flex flex-col h-full py-5 px-3 gap-1">

        {/* Logo / Brand */}
        <div
          onClick={handleLogoTap}
          className="flex items-center gap-3 px-2 mb-5 cursor-pointer select-none group/brand transition-transform active:scale-95"
          title="Veridia"
        >
          {businessLogoUrl ? (
            <img src={businessLogoUrl} alt={businessName} className="h-7 w-7 rounded-lg object-cover shrink-0" />
          ) : (
            <div className="w-8 h-8 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0 group-hover/brand:border-white/25">
              <span className="text-white text-[10px] font-bold">{businessName.slice(0, 2).toUpperCase()}</span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-white text-xs font-bold leading-tight truncate">{businessName}</p>
            <p className="text-white/35 text-[10px] leading-tight mt-0.5">Panel de control</p>
          </div>
        </div>

        {/* Section label */}
        <p className="text-white/25 text-[10px] font-semibold uppercase tracking-widest px-3 mb-1">
          Navegación
        </p>

        {/* Main nav */}
        <div className="flex flex-col gap-0.5 flex-1">
          {NAV_ITEMS.filter(visible).map(item => (
            <NavButton
              key={item.path}
              item={item}
              active={active === item.path}
              onClick={() => navigate(item.path)}
            />
          ))}
        </div>

        {/* Bottom nav */}
        {BOTTOM_ITEMS.some(visible) && (
          <>
            <div className="border-t border-white/8 my-2" />
            <p className="text-white/25 text-[10px] font-semibold uppercase tracking-widest px-3 mb-1">
              Admin
            </p>
            <div className="flex flex-col gap-0.5">
              {BOTTOM_ITEMS.filter(visible).map(item => (
                <NavButton
                  key={item.path}
                  item={item}
                  active={active === item.path}
                  onClick={() => navigate(item.path)}
                />
              ))}
            </div>
          </>
        )}

        {/* User section */}
        <div className="border-t border-white/8 pt-3 mt-2">
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-white/5 transition-colors group">
            <div className="w-7 h-7 rounded-full bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
              <span className="text-white/80 text-[10px] font-bold">{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white/80 text-[11px] font-semibold truncate leading-tight">{auth.user?.email}</p>
              <p className="text-white/30 text-[10px] leading-tight mt-0.5 truncate">
                {roleLabel[auth.user?.role ?? ''] ?? auth.user?.role}
              </p>
            </div>
            <button
              onClick={auth.logout}
              aria-label="Cerrar sesión"
              className="text-white/25 hover:text-red-400 transition-colors p-1 rounded-lg hover:bg-red-500/10 shrink-0"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </aside>
  )
}

function NavButton({ item, active, onClick }: { item: NavItem; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all text-xs font-medium ${
        active
          ? 'text-white bg-white/12 border border-white/15'
          : 'text-white/45 hover:text-white/80 hover:bg-white/6 border border-transparent'
      }`}
    >
      <span className={active ? 'text-emerald-400' : 'text-white/30 group-hover:text-white/60'}>
        {item.icon}
      </span>
      {item.title}
      {active && (
        <div className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
      )}
    </button>
  )
}
